import React, { useState, useEffect } from 'react';

const PeakActivityHeatmap = ({ interval, apiBaseUrl }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const HOURS = Array.from({ length: 24 }, (_, i) => i);

  useEffect(() => {
    fetchPeakActivityHours();
  }, [interval]);

  const fetchPeakActivityHours = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiBaseUrl}/chat_safety/peak_activity_hours/${interval}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch peak activity hours data');
      }

      const result = await response.json();
      
      // Transform the data into a heatmap matrix
      const heatmapData = result.map(item => ({
        dayOfWeek: item[0], // 0-6 (Sunday-Saturday)
        hourOfDay: item[1], // 0-23
        alertCount: item[2] || 0,
      }));

      setData(heatmapData);
    } catch (err) {
      console.error('Error fetching peak activity hours:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAlertCount = (day, hour) => {
    const cell = data.find(d => d.dayOfWeek === day && d.hourOfDay === hour);
    return cell ? cell.alertCount : 0;
  };

  const getMaxCount = () => {
    if (data.length === 0) return 1;
    return Math.max(...data.map(d => d.alertCount), 1);
  };

  const getHeatColor = (count) => {
    if (count === 0) return '#f7fafc';
    
    const maxCount = getMaxCount();
    const intensity = count / maxCount;
    
    // Color gradient from light purple to dark red
    if (intensity <= 0.2) return '#e9d5ff'; // Very light purple
    if (intensity <= 0.4) return '#c084fc'; // Light purple
    if (intensity <= 0.6) return '#a855f7'; // Purple
    if (intensity <= 0.8) return '#f472b6'; // Pink
    return '#ef4444'; // Red
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: '#718096',
        fontSize: '14px',
        gap: '12px',
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #e2e8f0',
          borderTopColor: '#667eea',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <span>Loading activity heatmap...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: '#e53e3e',
        fontSize: '14px',
      }}>
        <p>Error loading heatmap: {error}</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: '#718096',
        fontSize: '14px',
      }}>
        <p>No activity data available for this period</p>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      overflowX: 'auto',
      background: 'white',
      borderRadius: '8px',
      padding: '20px',
    }}>
      {/* Title and Legend */}
      <div style={{
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div>
          <h3 style={{
            margin: '0 0 4px 0',
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748',
          }}>
            Alert Activity by Day & Hour
          </h3>
          <p style={{
            margin: 0,
            fontSize: '12px',
            color: '#718096',
          }}>
            Hover over cells to see exact counts
          </p>
        </div>
        
        {/* Legend */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '11px',
          color: '#718096',
        }}>
          <span>Low</span>
          <div style={{ display: 'flex', gap: '2px' }}>
            <div style={{ width: '20px', height: '12px', background: '#e9d5ff', borderRadius: '2px' }}></div>
            <div style={{ width: '20px', height: '12px', background: '#c084fc', borderRadius: '2px' }}></div>
            <div style={{ width: '20px', height: '12px', background: '#a855f7', borderRadius: '2px' }}></div>
            <div style={{ width: '20px', height: '12px', background: '#f472b6', borderRadius: '2px' }}></div>
            <div style={{ width: '20px', height: '12px', background: '#ef4444', borderRadius: '2px' }}></div>
          </div>
          <span>High</span>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '60px repeat(24, 1fr)',
        gap: '4px',
        minWidth: '900px',
      }}>
        {/* Header - Hours */}
        <div></div>
        {HOURS.map(hour => (
          <div
            key={`hour-${hour}`}
            style={{
              fontSize: '10px',
              color: '#718096',
              textAlign: 'center',
              fontWeight: '500',
              padding: '4px 0',
            }}
          >
            {hour % 3 === 0 ? formatHour(hour).split(' ')[0] : ''}
          </div>
        ))}

        {/* Rows - Days */}
        {DAYS.map((day, dayIndex) => (
          <React.Fragment key={`day-${dayIndex}`}>
            {/* Day Label */}
            <div style={{
              fontSize: '12px',
              color: '#4a5568',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              paddingRight: '8px',
            }}>
              {day}
            </div>
            
            {/* Hour Cells */}
            {HOURS.map(hour => {
              const count = getAlertCount(dayIndex, hour);
              const color = getHeatColor(count);
              
              return (
                <div
                  key={`cell-${dayIndex}-${hour}`}
                  style={{
                    aspectRatio: '1',
                    background: color,
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: '1px solid #e2e8f0',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.15)';
                    e.currentTarget.style.zIndex = '10';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.zIndex = '1';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  title={`${day} ${formatHour(hour)}: ${count} alert${count !== 1 ? 's' : ''}`}
                >
                  <span style={{
                    fontSize: '9px',
                    fontWeight: '600',
                    color: count > getMaxCount() * 0.5 ? 'white' : '#4a5568',
                  }}>
                    {count > 0 ? count : ''}
                  </span>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {/* Summary Stats */}
      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: '#f7fafc',
        borderRadius: '8px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
      }}>
        <div>
          <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: '#718096', textTransform: 'uppercase', fontWeight: '600' }}>
            Total Alerts
          </p>
          <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#2d3748' }}>
            {data.reduce((sum, d) => sum + d.alertCount, 0)}
          </p>
        </div>
        <div>
          <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: '#718096', textTransform: 'uppercase', fontWeight: '600' }}>
            Peak Activity
          </p>
          <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#2d3748' }}>
            {(() => {
              const peak = data.reduce((max, d) => d.alertCount > max.alertCount ? d : max, data[0]);
              return peak ? `${DAYS[peak.dayOfWeek]} ${formatHour(peak.hourOfDay)}` : 'N/A';
            })()}
          </p>
        </div>
        <div>
          <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: '#718096', textTransform: 'uppercase', fontWeight: '600' }}>
            Peak Count
          </p>
          <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#e53e3e' }}>
            {getMaxCount()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PeakActivityHeatmap;